import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  useTheme,
  Stack,
  Typography,
  Chip,
  Paper,
  Button,
  Popover,
  Divider,
  SxProps,
  alpha,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import EventIcon from "@mui/icons-material/Event";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useColorMode } from "../../../context/ColorModeContext";
import { OfficerFilter } from "../hooks/useListVideos";

// Enable dayjs plugins for date comparison
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/** Which date-range dropdown is currently open. */
type DateFilterName = "uploadedDate" | "recordedDate";

/** Which filter dropdown is currently open (date ranges + uploader). */
type FilterPopoverName = DateFilterName | "uploadedBy";

type QuickRange = "today" | "last7" | "last30" | "clear";

interface VideoFiltersProps {
  searchTerm: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  // Upload-date (createdAt) range.
  createdFrom: Dayjs | null;
  setCreatedFrom: (date: Dayjs | null) => void;
  createdTo: Dayjs | null;
  setCreatedTo: (date: Dayjs | null) => void;
  // Recorded-date (recordedAt) range — independent of the upload range.
  recordedFrom: Dayjs | null;
  setRecordedFrom: (date: Dayjs | null) => void;
  recordedTo: Dayjs | null;
  setRecordedTo: (date: Dayjs | null) => void;
  sortBy: "createdAt" | "recordedAt";
  sortOrder: "asc" | "desc";
  /** Clicking a chip: re-click toggles direction, a new field selects it. */
  onSortChange: (sortBy: "createdAt" | "recordedAt") => void;
  /** Clears the search box — used by the "Search" active-filter chip and by
   *  "Clear all". (onSearchChange only handles user typing.) */
  onClearSearch: () => void;
  /** Grays out search and date filters (they don't apply to the Not Fetched
   *  tab). Sorting stays enabled on both tabs. */
  disabled?: boolean;
  /** Shows the "Uploaded By" filter (Ready to use + Shared With Me tabs). */
  showUploaderFilter?: boolean;
  /** Officer options for the uploader filter — name shown, id sent. */
  uploaderOptions?: OfficerFilter[];
  /** Currently-selected uploaders. */
  selectedUploaders?: OfficerFilter[];
  /** Fired when the uploader selection changes. */
  onUploaderChange?: (next: OfficerFilter[]) => void;
}

/** One date dimension's value + setters, resolved for whichever dropdown is open. */
interface ActiveDateDim {
  title: string;
  from: Dayjs | null;
  setFrom: (date: Dayjs | null) => void;
  to: Dayjs | null;
  setTo: (date: Dayjs | null) => void;
}

export const VideoFilters: React.FC<VideoFiltersProps> = ({
  searchTerm,
  onSearchChange,
  createdFrom,
  setCreatedFrom,
  createdTo,
  setCreatedTo,
  recordedFrom,
  setRecordedFrom,
  recordedTo,
  setRecordedTo,
  sortBy,
  sortOrder,
  onSortChange,
  onClearSearch,
  disabled = false,
  showUploaderFilter = false,
  uploaderOptions = [],
  selectedUploaders = [],
  onUploaderChange,
}) => {
  const theme = useTheme();
  const vars = theme;
  const { mode } = useColorMode();

  const directionIcon =
    sortOrder === "asc" ? (
      <ArrowUpwardIcon sx={{ fontSize: "1.125rem !important" }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: "1.125rem !important" }} />
    );

  // Single shared popover state so only one date dropdown is open at a time,
  // mirroring the My Reports (home) page filter buttons.
  const [filterPopover, setFilterPopover] = useState<{
    name: FilterPopoverName | null;
    anchorEl: HTMLElement | null;
  }>({ name: null, anchorEl: null });

  const handleFilterClick = (
    event: React.MouseEvent<HTMLElement>,
    name: FilterPopoverName,
  ) => {
    setFilterPopover({
      name: filterPopover.name === name ? null : name,
      anchorEl: event.currentTarget,
    });
  };

  const handleClosePopover = () =>
    setFilterPopover({ name: null, anchorEl: null });

  // Date filters don't apply on the Other-footage tab. If a dropdown is open
  // when the tab becomes disabled, close it (and clear its name) so it can't
  // linger interactive — and so returning to the ingested tab doesn't reopen it.
  useEffect(() => {
    if (disabled) setFilterPopover({ name: null, anchorEl: null });
  }, [disabled]);

  // The uploader filter isn't shown on every tab (e.g. My Evidence). If its
  // dropdown is open when the tab hides it, close it so it can't linger anchored
  // to an unmounted button.
  useEffect(() => {
    if (!showUploaderFilter) {
      setFilterPopover((prev) =>
        prev.name === "uploadedBy" ? { name: null, anchorEl: null } : prev,
      );
    }
  }, [showUploaderFilter]);

  const uploadedActive = Boolean(createdFrom || createdTo);
  const recordedActive = Boolean(recordedFrom || recordedTo);
  const uploaderActive = selectedUploaders.length > 0;

  // Resolve the active dropdown to its backing range. Labels read
  // "Uploaded"/"Recorded"; the dimensions are createdAt / recordedAt.
  const activeDim: ActiveDateDim | null =
    filterPopover.name === "uploadedDate"
      ? {
          title: "Uploaded Date",
          from: createdFrom,
          setFrom: setCreatedFrom,
          to: createdTo,
          setTo: setCreatedTo,
        }
      : filterPopover.name === "recordedDate"
        ? {
            title: "Recorded Date",
            from: recordedFrom,
            setFrom: setRecordedFrom,
            to: recordedTo,
            setTo: setRecordedTo,
          }
        : null;

  // Choosing a "to" earlier than the current "from" clears the now-invalid "from".
  const handleToChange = (dim: ActiveDateDim, newValue: Dayjs | null) => {
    dim.setTo(newValue);
    if (newValue && dim.from && dim.from.isAfter(newValue)) {
      dim.setFrom(null);
    }
  };

  const applyQuickRange = (dim: ActiveDateDim, range: QuickRange) => {
    const today = dayjs();
    if (range === "today") {
      dim.setFrom(today);
      dim.setTo(today);
    } else if (range === "last7") {
      dim.setFrom(today.subtract(6, "day"));
      dim.setTo(today);
    } else if (range === "last30") {
      dim.setFrom(today.subtract(29, "day"));
      dim.setTo(today);
    } else {
      dim.setFrom(null);
      dim.setTo(null);
    }
  };

  // Filter button styling — matches ReportFilters on the home page.
  const filterButtonStyle = (isActive: boolean): SxProps => ({
    borderColor: isActive ? "transparent" : alpha(vars.palette.text.primary, 0.3),
    color: isActive ? "common.white" : "text.primary",
    backgroundColor: isActive ? "primary.dark" : "transparent",
    borderRadius: "8px",
    textTransform: "none",
    fontWeight: isActive ? 700 : 500,
    fontSize: { xs: "12px", lg: "14px", xl: "15px" },
    minHeight: 44,
    "&:hover": {
      backgroundColor: isActive ? "primary.main" : "action.hover",
    },
  });

  const renderCountBadge = (active: boolean) =>
    active ? (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 22,
          height: 22,
          px: 0.75,
          borderRadius: "999px",
          backgroundColor: "background.paper",
          color: "primary.dark",
          fontWeight: 700,
          fontSize: "0.85rem",
          ml: 1,
        }}
      >
        1
      </Box>
    ) : null;

  // Active-filter chips + "Clear all", mirroring the home page. Sort is left
  // untouched (it lives in its own row, exactly as on home).
  const fmt = (d: Dayjs | null) => (d ? dayjs(d).format("MM/DD/YY") : "...");
  const activeChips: { key: string; label: string; onDelete: () => void }[] =
    [];
  if (searchTerm.trim()) {
    activeChips.push({
      key: "search",
      label: `Search: ${searchTerm}`,
      onDelete: onClearSearch,
    });
  }
  if (createdFrom || createdTo) {
    activeChips.push({
      key: "uploadedRange",
      label: `Uploaded: ${fmt(createdFrom)} - ${fmt(createdTo)}`,
      onDelete: () => {
        setCreatedFrom(null);
        setCreatedTo(null);
      },
    });
  }
  if (recordedFrom || recordedTo) {
    activeChips.push({
      key: "recordedRange",
      label: `Recorded: ${fmt(recordedFrom)} - ${fmt(recordedTo)}`,
      onDelete: () => {
        setRecordedFrom(null);
        setRecordedTo(null);
      },
    });
  }
  if (showUploaderFilter && uploaderActive) {
    activeChips.push({
      key: "uploadedBy",
      label: `Uploaded By: ${selectedUploaders.map((u) => u.name).join(", ")}`,
      onDelete: () => onUploaderChange?.([]),
    });
  }

  const handleClearAll = () => {
    onClearSearch();
    setCreatedFrom(null);
    setCreatedTo(null);
    setRecordedFrom(null);
    setRecordedTo(null);
    onUploaderChange?.([]);
  };

  return (
    <Grid size={{ xs: 12 }}>
      {/* Search + filters card — mirrors the My Reports (home) page layout. */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          bgcolor: "background.paper",
          border: `1px solid ${alpha(theme.palette.text.primary, 0.15)}`,
          borderRadius: 2,
        }}
      >
        <Stack spacing={2.5}>
          {/* Full-width search */}
          <TextField
            placeholder="Search videos by details, keywords..."
            value={searchTerm}
            onChange={onSearchChange}
            disabled={disabled}
            fullWidth
            size="medium"
            sx={{ minHeight: 56, backgroundColor: "background.default" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          {/* Filters row: labelled, with the two independent date-range dropdowns. */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FilterListIcon color="action" />
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Filters:
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
              {/* Uploaded Date */}
              <Button
                variant={uploadedActive ? "contained" : "outlined"}
                size="medium"
                startIcon={<EventIcon />}
                endIcon={
                  <KeyboardArrowDownIcon
                    sx={{ color: uploadedActive ? "common.white" : undefined }}
                  />
                }
                onClick={(e) => handleFilterClick(e, "uploadedDate")}
                disabled={disabled}
                sx={filterButtonStyle(uploadedActive)}
              >
                Uploaded Date
                {renderCountBadge(uploadedActive)}
              </Button>

              {/* Recorded Date */}
              <Button
                variant={recordedActive ? "contained" : "outlined"}
                size="medium"
                startIcon={<EventIcon />}
                endIcon={
                  <KeyboardArrowDownIcon
                    sx={{ color: recordedActive ? "common.white" : undefined }}
                  />
                }
                onClick={(e) => handleFilterClick(e, "recordedDate")}
                disabled={disabled}
                sx={filterButtonStyle(recordedActive)}
              >
                Recorded Date
                {renderCountBadge(recordedActive)}
              </Button>

              {/* Uploaded By — only on the Ready to use + Shared With Me tabs. */}
              {showUploaderFilter && (
                <Button
                  variant={uploaderActive ? "contained" : "outlined"}
                  size="medium"
                  startIcon={<PersonIcon />}
                  endIcon={
                    <KeyboardArrowDownIcon
                      sx={{
                        color: uploaderActive ? "common.white" : undefined,
                      }}
                    />
                  }
                  onClick={(e) => handleFilterClick(e, "uploadedBy")}
                  disabled={disabled}
                  sx={filterButtonStyle(uploaderActive)}
                >
                  Uploaded By
                  {uploaderActive && (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 22,
                        height: 22,
                        px: 0.75,
                        borderRadius: "999px",
                        backgroundColor: "background.paper",
                        color: "primary.dark",
                        fontWeight: 700,
                        fontSize: "0.85rem",
                        ml: 1,
                      }}
                    >
                      {selectedUploaders.length}
                    </Box>
                  )}
                </Button>
              )}
            </Stack>
          </Box>

          {/* Active-filter chips + Clear all — mirrors the home page. Hidden on
              the unfetched tab, where filters don't apply (reset is meaningless). */}
          {activeChips.length > 0 && !disabled && (
            <>
              <Divider
                sx={{ borderColor: alpha(vars.palette.text.secondary, 0.2) }}
              />
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Button
                  variant="text"
                  color="inherit"
                  startIcon={<CloseIcon fontSize="small" />}
                  onClick={handleClearAll}
                  disabled={disabled}
                  sx={{
                    px: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Clear all ({activeChips.length})
                </Button>

                {activeChips.map((chip) => (
                  <Chip
                    key={chip.key}
                    label={chip.label}
                    onDelete={chip.onDelete}
                    disabled={disabled}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 600,
                      backgroundColor: alpha(vars.palette.primary.dark, 0.08),
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </Stack>
      </Paper>

      {/* Sort row — sits below the card, as on the home page. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          width: "100%",
          flexWrap: "wrap",
        }}
      >
        {/* Sort by control */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: "text.secondary",
              mr: 0.5,
            }}
          >
            <SwapVertIcon sx={{ fontSize: "1.25rem" }} />
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{ whiteSpace: "nowrap" }}
            >
              Sort:
            </Typography>
          </Box>
          <Chip
            label="Uploaded Date"
            onClick={() => onSortChange("createdAt")}
            color={sortBy === "createdAt" ? "primary" : "default"}
            variant={sortBy === "createdAt" ? "filled" : "outlined"}
            icon={sortBy === "createdAt" ? directionIcon : undefined}
            clickable
            size="medium"
            sx={{
              fontSize: "0.9375rem",
              fontWeight: sortBy === "createdAt" ? 600 : 400,
              borderRadius: 2,
              transition: "all 0.2s ease",
              ...(sortBy !== "createdAt" && {
                borderColor: alpha(theme.palette.text.primary, 0.3),
                color: "text.primary",
                ...(mode === "dark" && {
                  bgcolor: alpha(theme.palette.text.primary, 0.06),
                }),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  borderColor: theme.palette.primary.main,
                },
              }),
            }}
          />
          <Chip
            label="Recorded Date"
            onClick={() => onSortChange("recordedAt")}
            color={sortBy === "recordedAt" ? "primary" : "default"}
            variant={sortBy === "recordedAt" ? "filled" : "outlined"}
            icon={sortBy === "recordedAt" ? directionIcon : undefined}
            clickable
            size="medium"
            sx={{
              fontSize: "0.9375rem",
              fontWeight: sortBy === "recordedAt" ? 600 : 400,
              borderRadius: 2,
              transition: "all 0.2s ease",
              ...(sortBy !== "recordedAt" && {
                borderColor: alpha(theme.palette.text.primary, 0.3),
                color: "text.primary",
                ...(mode === "dark" && {
                  bgcolor: alpha(theme.palette.text.primary, 0.06),
                }),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  borderColor: theme.palette.primary.main,
                },
              }),
            }}
          />
        </Stack>
      </Box>

      {/* Date-range dropdown — one Popover serves both buttons. */}
      <Popover
        open={Boolean(filterPopover.name) && !disabled}
        anchorEl={filterPopover.anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableRestoreFocus
        PaperProps={{
          sx: { p: 2, width: { xs: 320, xxl: 400 }, maxWidth: "95vw" },
        }}
      >
        {activeDim && (
          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight="bold">
              {activeDim.title}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label="Today"
                onClick={() => applyQuickRange(activeDim, "today")}
              />
              <Chip
                label="Last 7 days"
                onClick={() => applyQuickRange(activeDim, "last7")}
              />
              <Chip
                label="Last 30 days"
                onClick={() => applyQuickRange(activeDim, "last30")}
              />
            </Stack>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                <DatePicker
                  label="From"
                  value={activeDim.from}
                  onChange={(newValue) => activeDim.setFrom(newValue)}
                  maxDate={activeDim.to || dayjs()}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
                <DatePicker
                  label="To"
                  value={activeDim.to}
                  onChange={(newValue) => handleToChange(activeDim, newValue)}
                  maxDate={dayjs()}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </Stack>
            </LocalizationProvider>
            <Box display="flex" justifyContent="space-between">
              <Button
                size="small"
                onClick={() => applyQuickRange(activeDim, "clear")}
              >
                Clear
              </Button>
              <Button size="small" onClick={handleClosePopover}>
                Done
              </Button>
            </Box>
          </Stack>
        )}

        {filterPopover.name === "uploadedBy" && (
          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight="bold">
              Uploaded By
            </Typography>
            {uploaderOptions.length > 0 ? (
              <Autocomplete
                multiple
                open
                options={uploaderOptions}
                value={selectedUploaders}
                onChange={(_, newValue) => onUploaderChange?.(newValue)}
                onClose={(_, reason) => {
                  // The always-open Autocomplete swallows the Escape key, so
                  // forward it to close the parent popover.
                  if (reason === "escape") handleClosePopover();
                }}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disableCloseOnSelect
                disablePortal
                limitTags={3}
                forcePopupIcon={false}
                ListboxProps={{ style: { maxHeight: 250 } }}
                slotProps={{
                  popper: {
                    sx: {
                      position: "relative !important",
                      transform: "none !important",
                    },
                    disablePortal: true,
                  },
                  paper: {
                    sx: { boxShadow: "none", mt: 1 },
                  },
                }}
                renderOption={(props, option, { selected }) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li key={key} {...otherProps}>
                      <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                      {option.name}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search names..."
                    size="small"
                    autoFocus
                  />
                )}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No uploaders found
              </Typography>
            )}
            <Box display="flex" justifyContent="space-between">
              <Button size="small" onClick={() => onUploaderChange?.([])}>
                Clear
              </Button>
              <Button size="small" onClick={handleClosePopover}>
                Done
              </Button>
            </Box>
          </Stack>
        )}
      </Popover>
    </Grid>
  );
};
